import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  inject,
  OnDestroy,
  output,
  signal,
  viewChild,
  viewChildren,
} from '@angular/core';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { ReactiveFormsModule, UntypedFormControl } from '@angular/forms';
import { catchError, debounceTime, distinctUntilChanged, map, switchMap, tap } from 'rxjs/operators';
import { fromEvent, of } from 'rxjs';
import { SEARCH_QUERY_PARAM } from '../../../constants';
import { Api } from '@api/api';
import { autocomplete } from '@api/functions';
import type { AutocompleteItemDto } from '@api/models';
import { AutocompleteItemType } from '@api/models';
import { DeviceService } from '../../../services/device-detector/device.service';
import { PreventScrollService } from '../../../services/prevent-scroll.service';
import { LanguageService } from '../../../services/language/language.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { PreloaderComponent } from '../../preloader/preloader.component';
import { RouterLinkWithLang } from '../../../directives/router-link-with-lang/router-link-with-lang.directive';
import { TranslatePipe } from '@ngx-translate/core';
import { resolveHttpErrorMessage } from '@/helpers/resolve-http-error-message.helper';
import { ClickOutsideDirective } from "@/directives/click-outside.directive";

@Component({
  selector: 'search-bar',
  templateUrl: './search-bar.component.html',
  styleUrls: ['./search-bar.component.scss'],
  imports: [
    ReactiveFormsModule,
    PreloaderComponent,
    TranslatePipe,
    RouterLink,
    RouterLinkWithLang,
    ClickOutsideDirective,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SearchBarComponent implements AfterViewInit, OnDestroy {
  private deviceService = inject(DeviceService);
  private route = inject(ActivatedRoute);
  private api = inject(Api);
  private preventScrollService = inject(PreventScrollService);
  private languageService = inject(LanguageService);
  private destroyRef = inject(DestroyRef);

  readonly inputRef = viewChild<ElementRef>('inputRef');
  readonly searchLinkRef = viewChild<ElementRef<HTMLAnchorElement>>('searchLink');
  readonly resultLinkRefs = viewChildren<ElementRef<HTMLAnchorElement>>('resultLink');
  readonly isSearchBarInFocus = output<boolean>();

  readonly isInFocus = signal(false);
  readonly searchResults = signal<AutocompleteItemDto[] | null>(null);
  readonly isSearchInProgress = signal(false);
  readonly searchError = signal<string | null>(null);
  readonly activeIndex = signal<number | null>(null);

  protected searchControl: UntypedFormControl;
  autocompleteItemTypes = AutocompleteItemType;

  constructor() {
    const initialValue = this.route.snapshot.queryParamMap.get(SEARCH_QUERY_PARAM) || '';
    this.searchControl = new UntypedFormControl(initialValue);
    this.handleAutocomplete();
  }

  ngAfterViewInit(): void {
    this.handleHotkeys();
  }

  ngOnDestroy(): void {
    this.preventScrollService.isEnabled$.next(false);
  }

  onSearchClick(event: MouseEvent): void {
    if (!this.searchControl.value?.trim()) {
      event.preventDefault();
      return;
    }

    this.searchResults.set(null);
    this.handleSearchBarInFocus(false);
  }

  get searchLinkQueryParams(): Record<string, string> | null {
    const value = this.searchControl.value?.trim();
    return value ? { [SEARCH_QUERY_PARAM]: value } : null;
  }

  get searchLinkCommands(): string[] | null {
    const value = this.searchControl.value?.trim();
    if (!value) {
      return null;
    }

    return ['/', this.languageService.getCurrentRouteLang(), 'search'].filter(Boolean);
  }

  handleSearchBarInFocus(isSearchBarInFocus: boolean): void {
    this.isInFocus.set(isSearchBarInFocus);
    this.isSearchBarInFocus.emit(isSearchBarInFocus);
    if (this.deviceService.isMobile()) {
      this.preventScrollService.isEnabled$.next(isSearchBarInFocus);
    }
  }

  clearInput(): void {
    this.searchControl.setValue('');
    this.searchResults.set(null);
    this.inputRef()?.nativeElement?.focus();
  }

  private handleAutocomplete(): void {
    if (!this.deviceService.isPlatformBrowser()) { return; }

    this.searchControl.valueChanges.pipe(
      takeUntilDestroyed(this.destroyRef),
      debounceTime(200),
      distinctUntilChanged(),
      map(value => value.trim()),
      tap(() => this.isSearchInProgress.set(true)),
      switchMap(query => query ? this.api.invoke(autocomplete, { query }) : of({ data: null })),
      catchError((err, caught) => {
        this.searchResults.set(null);
        this.searchError.set(resolveHttpErrorMessage(err));
        this.isSearchInProgress.set(false);
        return caught;
      })
    ).subscribe({
      next: response => {
        this.searchResults.set(response.data);
        this.searchError.set(null);
        this.isSearchInProgress.set(false);
        this.activeIndex.set(null);
      }
    });
  }

  private handleHotkeys(): void {
    if (!this.deviceService.isPlatformBrowser()) { return; }

    const inputEl = this.inputRef()?.nativeElement;
    if (!inputEl) { return; }

    fromEvent<KeyboardEvent>(inputEl, 'keydown').pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe({
      next: event => {
        const results = this.searchResults();
        const idx = this.activeIndex();

        switch (event.key) {
          case 'Escape':
            inputEl.blur();
            this.handleSearchBarInFocus(false);
            break;
          case 'Enter':
            if (idx === null) {
              this.searchLinkRef()?.nativeElement.click();
            } else {
              event.preventDefault();
              this.resultLinkRefs()[idx]?.nativeElement.click();
            }
            break;
          case 'ArrowDown':
            event.preventDefault();
            if (idx === results?.length - 1 || idx === null) {
              this.activeIndex.set(0);
            } else {
              this.activeIndex.update(v => (v ?? 0) + 1);
            }
            break;
          case 'ArrowUp':
            event.preventDefault();
            if (idx === 0 || idx === null) {
              this.activeIndex.set((results?.length ?? 1) - 1);
            } else {
              this.activeIndex.update(v => (v ?? 0) - 1);
            }
            break;
        }
      }
    });
  }

  getResultImage(item: AutocompleteItemDto): string {
    return item.mediaUrl ? item.mediaUrl : '/assets/images/no-img.jpg';
  }
}
